import { ParsedLocation } from "node_modules/@tanstack/router-core/dist/esm/location";

const PROMO_MESSAGE_KEY = "promoMessage";
const REDIRECT_PATH_KEY = "authRedirectPath";
const REDIRECT_SEARCH_KEY = "authRedirectSearch";
const MOST_RECENT_PATH_KEY = "mostRecentRoute";
const MOST_RECENT_SEARCH_KEY = "mostRecentSearch";
const CODE_REQUEST_KEY_PREFIX = "codeRequestTimestamp";
const CODE_EXPIRY_MS = 3 * 60 * 1000;
const OTC_COOLDOWN_KEY_PREFIX = "otcRequestTimestamp:";
const OTC_COOLDOWN_MS = 30 * 1000;
const IMPERSONATE_EMAIL_KEY = "impersonate_email";
const PENDING_SUBSCRIPTION_KEY = "pending_subscription";
const LAST_AUTH_KEY = "lastAuth";
const AUTH_HANDOFF_KEY = "authHandoff";

export type LastAuthMethod = "otp" | "google" | "apple";

export function saveMostRecentRoute(location: ParsedLocation) {
  try {
    const { pathname, search } = location;
    const isAuthPath =
      pathname.startsWith("/auth/") || pathname.startsWith("/oauth2/");

    if (isAuthPath) return;

    // Strip the auth modal param so redirects after social login don't reopen the modal
    const { auth: _, ...cleanSearch } = search as Record<string, unknown>;

    localStorage.setItem(MOST_RECENT_PATH_KEY, pathname);
    localStorage.setItem(MOST_RECENT_SEARCH_KEY, JSON.stringify(cleanSearch));
  } catch {
    // noop
  }
}

export function getMostRecentRoute(): {
  pathname: string;
  search: object;
} | null {
  try {
    const pathname = localStorage.getItem(MOST_RECENT_PATH_KEY);
    const search = localStorage.getItem(MOST_RECENT_SEARCH_KEY);

    if (!pathname || !search) return null;

    return {
      pathname,
      search: JSON.parse(search),
    };
  } catch {
    return null;
  }
}

export function clearMostRecentRoute() {
  try {
    localStorage.removeItem(MOST_RECENT_PATH_KEY);
    localStorage.removeItem(MOST_RECENT_SEARCH_KEY);
  } catch {
    // noop
  }
}

export function savePromoMessage(message: string) {
  try {
    sessionStorage.setItem(PROMO_MESSAGE_KEY, message);
  } catch {
    // noop
  }
}

export function getPromoMessage(): string | null {
  try {
    return sessionStorage.getItem(PROMO_MESSAGE_KEY);
  } catch {
    return null;
  }
}

export function clearPromoMessage() {
  try {
    sessionStorage.removeItem(PROMO_MESSAGE_KEY);
  } catch {
    // noop
  }
}

export function saveRedirect(publicHref: string) {
  try {
    const { pathname, searchParams } = new URL(
      publicHref,
      window.location.origin,
    );

    localStorage.setItem(REDIRECT_PATH_KEY, pathname);
    localStorage.setItem(
      REDIRECT_SEARCH_KEY,
      JSON.stringify(Object.fromEntries(searchParams)),
    );
  } catch {
    // noop
  }
}

export function getRedirect(): { pathname: string; search: object } | null {
  try {
    const pathname = localStorage.getItem(REDIRECT_PATH_KEY);
    const search = localStorage.getItem(REDIRECT_SEARCH_KEY);

    if (!pathname || !search) return null;

    return {
      pathname,
      search: JSON.parse(search),
    };
  } catch {
    return null;
  }
}

export function clearRedirect() {
  try {
    localStorage.removeItem(REDIRECT_PATH_KEY);
    localStorage.removeItem(REDIRECT_SEARCH_KEY);
  } catch {
    // noop
  }
}

/**
 * Saves the current timestamp for the user's last verification code request.
 * @param email {string}
 */
export function saveCodeRequestTimestamp(email: string) {
  try {
    localStorage.setItem(
      CODE_REQUEST_KEY_PREFIX + email,
      Date.now().toString(),
    );
  } catch {
    // noop
  }
}

export function getCodeRequestCooldownMs(email: string): number {
  try {
    const storedTime = localStorage.getItem(CODE_REQUEST_KEY_PREFIX + email);
    if (!storedTime) return 0;

    const lastRequestTime = parseInt(storedTime, 10);
    const elapsedTime = Date.now() - lastRequestTime;

    return Math.max(CODE_EXPIRY_MS - elapsedTime, 0);
  } catch {
    return 0;
  }
}

export function saveOtcRequestTimestamp(email: string) {
  try {
    localStorage.setItem(
      OTC_COOLDOWN_KEY_PREFIX + email,
      Date.now().toString(),
    );
  } catch {
    // noop
  }
}

export function getOtcCooldownMs(email: string): number {
  try {
    const storedTime = localStorage.getItem(OTC_COOLDOWN_KEY_PREFIX + email);
    if (!storedTime) return 0;

    const lastRequestTime = parseInt(storedTime, 10);
    const elapsedTime = Date.now() - lastRequestTime;

    return Math.max(OTC_COOLDOWN_MS - elapsedTime, 0);
  } catch {
    return 0;
  }
}

export function saveImpersonateEmail(email: string) {
  try {
    localStorage.setItem(IMPERSONATE_EMAIL_KEY, email);
  } catch {
    // noop
  }
}

export function getImpersonateEmail(): string | null {
  try {
    return localStorage.getItem(IMPERSONATE_EMAIL_KEY);
  } catch {
    return null;
  }
}

export function clearImpersonateEmail() {
  try {
    localStorage.removeItem(IMPERSONATE_EMAIL_KEY);
  } catch {
    // noop
  }
}

export function savePendingSubscription(subscriptionIdentifier: string) {
  try {
    localStorage.setItem(
      PENDING_SUBSCRIPTION_KEY,
      JSON.stringify({
        subscriptionIdentifier,
        timestamp: Date.now(),
      }),
    );
  } catch {
    // noop
  }
}

export function getPendingSubscription() {
  try {
    return localStorage.getItem(PENDING_SUBSCRIPTION_KEY);
  } catch {
    return null;
  }
}

export function clearPendingSubscription() {
  try {
    localStorage.removeItem(PENDING_SUBSCRIPTION_KEY);
  } catch {
    // noop
  }
}

export function saveLastAuth(method: LastAuthMethod, email: string) {
  try {
    localStorage.setItem(LAST_AUTH_KEY, JSON.stringify({ method, email }));
  } catch {
    // noop
  }
}

export function getLastAuth(): {
  method: LastAuthMethod;
  email: string;
} | null {
  try {
    const raw = localStorage.getItem(LAST_AUTH_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// ─── Auth Handoff (landing page card → auth modal) ───

export type AuthHandoff = {
  email: string;
  authMethods?: string[];
  initialPhase?: "signup" | "password";
};

export function setAuthHandoff(data: AuthHandoff) {
  try {
    sessionStorage.setItem(AUTH_HANDOFF_KEY, JSON.stringify(data));
  } catch {
    // noop
  }
}

export function consumeAuthHandoff(): AuthHandoff | null {
  try {
    const raw = sessionStorage.getItem(AUTH_HANDOFF_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(AUTH_HANDOFF_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// ─── Track upload timestamps (time-to-publish analytics, LUT-775) ───

const UPLOAD_STARTED_KEY_PREFIX = "trackUploadStartedAt:";

export function saveTrackUploadStartedAt(trackId: string) {
  try {
    localStorage.setItem(
      UPLOAD_STARTED_KEY_PREFIX + trackId,
      Date.now().toString(),
    );
  } catch {
    // noop
  }
}

export function consumeTrackUploadStartedAt(trackId: string): number | null {
  try {
    const raw = localStorage.getItem(UPLOAD_STARTED_KEY_PREFIX + trackId);
    if (!raw) return null;
    localStorage.removeItem(UPLOAD_STARTED_KEY_PREFIX + trackId);
    const timestamp = parseInt(raw, 10);
    return Number.isNaN(timestamp) ? null : timestamp;
  } catch {
    return null;
  }
}

// ─── Pending Email Verification (logged-out verify link flow) ───

const PENDING_EMAIL_VERIFICATION_CODE_KEY = "pendingEmailVerificationCode";

export type PendingEmailVerification = { code: string; email: string | null };

// Carries the address the code was issued for, so it can be discarded rather
// than applied to whichever account happens to sign in next.
export function savePendingEmailVerification(
  code: string,
  email: string | null,
) {
  try {
    sessionStorage.setItem(
      PENDING_EMAIL_VERIFICATION_CODE_KEY,
      JSON.stringify({ code, email }),
    );
  } catch {
    // noop
  }
}

export function consumePendingEmailVerification(): PendingEmailVerification | null {
  try {
    const raw = sessionStorage.getItem(PENDING_EMAIL_VERIFICATION_CODE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_EMAIL_VERIFICATION_CODE_KEY);

    try {
      const parsed = JSON.parse(raw) as Partial<PendingEmailVerification>;
      if (!parsed?.code) return null;
      return { code: parsed.code, email: parsed.email ?? null };
    } catch {
      // Older entries were a bare code string with no address attached.
      return { code: raw, email: null };
    }
  } catch {
    return null;
  }
}

export function emailsMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
